import React, { useCallback, useEffect, useRef, useState } from "react";
import * as S from "./DesktopHero.styles";
import { IHeroProps, ITourTooltip } from "./DesktopHero.types";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";

import { useTranslation } from "hooks/useTranslation";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";

enum AppVariant {
  Network = "Network",
  Protect = "Protect",
  Access = "Access",
  Connect = "Connect",
}

export const DesktopHero = ({ config }: Readonly<IHeroProps>) => {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);
  const activeVideoRef = useRef<Record<string, HTMLVideoElement>>({});
  useVideoCleanup(videoRef);
  useVideoCleanup(activeVideoRef);

  const [isCurrentlyHovering, setIsCurrentlyHovering] = useState(false);
  const [openTooltip, setOpenTooltip] = useState<ITourTooltip>(
    config?.tooltips?.[0],
  );
  const [canOpenTooltip, setCanOpenTooltip] = useState(false);

  const onActiveVideoPlaybackEnd = useCallback(
    (variant: AppVariant, index: number) => {
      if (variant !== openTooltip.variant) {
        return;
      }

      if (isCurrentlyHovering) {
        try {
          activeVideoRef.current[index].play();
        } catch (error) {
          console.error("Error playing video:", error);
        }

        return;
      }

      setOpenTooltip((prev) => {
        const nextIndex = prev.id + 1;
        const isLastIndex = nextIndex >= config.tooltips.length;
        const correctedIndex = isLastIndex ? 0 : nextIndex;

        return config.tooltips[correctedIndex];
      });
    },
    [config.tooltips, isCurrentlyHovering, openTooltip.variant],
  );

  const onInitialVideoPlaybackEnd = useCallback(() => {
    if (!config?.tooltips?.length) {
      return;
    }

    setCanOpenTooltip(true);
  }, [config]);

  const onUserMouseEntry = useCallback((tooltip: ITourTooltip) => {
    setOpenTooltip(tooltip);
    setIsCurrentlyHovering(true);
  }, []);

  const onUserMouseLeave = useCallback(() => {
    setIsCurrentlyHovering(false);
  }, []);

  useEffect(() => {
    if (!canOpenTooltip) {
      return;
    }

    if (activeVideoRef.current[openTooltip.id]) {
      try {
        activeVideoRef.current[openTooltip.id].currentTime = 0;
        activeVideoRef.current[openTooltip.id].play();
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }
  }, [canOpenTooltip, openTooltip.id]);

  return (
    <S.HeroContainer>
      <S.Wrapper>
        <S.ActiveVideoWrapper>
          <S.StyledTooltip
            title={openTooltip.variant}
            isOpen={canOpenTooltip}
            description={
              <S.StyledText>{t(openTooltip.description)}</S.StyledText>
            }
            className="how-unifi-tooltip"
            $position={{
              top: openTooltip.top,
              left: openTooltip.left,
              bottom: openTooltip.bottom,
              right: openTooltip.right,
            }}
            width={312}
            autoPosition
            portal
            position="bottom"
          />

          {(config.tooltips ?? []).map((tooltip, index) => (
            <div key={`${index}-${tooltip.id}`}>
              <S.ActiveVideo
                key={`${index}-${tooltip.id}`}
                ref={(element) => (activeVideoRef.current[index] = element)}
                src={tooltip.video}
                muted
                $isActive={tooltip.variant === openTooltip.variant}
                playsInline
                onEnded={() => onActiveVideoPlaybackEnd(tooltip.variant, index)}
              />

              <S.TooltipTrigger
                $position={{
                  top: tooltip.top,
                  left: tooltip.left,
                  bottom: tooltip.bottom,
                  right: tooltip.right,
                }}
                onMouseEnter={() => onUserMouseEntry(tooltip)}
                onMouseLeave={onUserMouseLeave}
              />
            </div>
          ))}

          <S.BackgroundVideo
            ref={videoRef}
            src={config?.src}
            muted
            playsInline
            autoPlay
            poster={getOptimizedImageUrl(
              absoluteAssetUrl(config?.poster),
              1920 * 2,
            )}
            $isVisible={!config?.tooltips?.length || !canOpenTooltip}
            onEnded={onInitialVideoPlaybackEnd}
          />
        </S.ActiveVideoWrapper>
      </S.Wrapper>

      <S.TooltipStyles />
    </S.HeroContainer>
  );
};
